import { useCallback } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PaymentAPIResponseMapper,
  PaymentManagementAPIResponseMapper,
  PaymentOverviewMapper,
} from '@/api/mapper/payment';
import { getPaymentsManagement, getPolicyPayments } from '@/api/service/payment';
import type { PageQuery } from '@/api/type/payment';

export const paymentKeys = {
  all: ['payments'] as const,
  management: (query: PageQuery) => [...paymentKeys.all, 'management', query] as const,
  byPolicy: (policyId: string) => [...paymentKeys.all, 'policy', policyId] as const,
};

/** One page of the payments table, searched, filtered and sorted by the backend. */
export function usePaymentsManagementQuery(query: PageQuery, enabled = true) {
  return useQuery({
    queryKey: paymentKeys.management(query),
    queryFn: async () => {
      const response = await getPaymentsManagement(query);
      return {
        rows: (response.data ?? []).map(PaymentManagementAPIResponseMapper),
        total: response.total ?? 0,
        overview: PaymentOverviewMapper(response.summary),
      };
    },
    // Keep the current page on screen while the next one loads.
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function usePolicyPaymentsQuery(policyId?: string | null, enabled = true) {
  return useQuery({
    queryKey: paymentKeys.byPolicy(policyId ?? 'none'),
    queryFn: async () => {
      const response = await getPolicyPayments(Number(policyId));
      return response.data.map(PaymentAPIResponseMapper);
    },
    enabled: enabled && Boolean(policyId),
  });
}

/** Refresh everything payment related (table, cards and per-policy history). */
export function useInvalidatePayments() {
  const queryClient = useQueryClient();

  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: paymentKeys.all }),
    [queryClient]
  );
}
